import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

const STORAGE_PREFIX = 'guided_tour_completed_';

const isCompleted = tourId => {
  try {
    return localStorage.getItem(`${STORAGE_PREFIX}${tourId}`) === '1';
  } catch {
    return false;
  }
};

const markCompleted = tourId => {
  try {
    localStorage.setItem(`${STORAGE_PREFIX}${tourId}`, '1');
  } catch {
    /* noop */
  }
};

const reset = tourId => {
  try {
    localStorage.removeItem(`${STORAGE_PREFIX}${tourId}`);
  } catch {
    /* noop */
  }
};

const waitForElement = (selector, timeout = 3000) =>
  new Promise(resolve => {
    if (!selector) {
      resolve(null);
      return;
    }
    const found = document.querySelector(selector);
    if (found) {
      resolve(found);
      return;
    }
    const observer = new MutationObserver(() => {
      const el = document.querySelector(selector);
      if (el) {
        observer.disconnect();
        resolve(el);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => {
      observer.disconnect();
      resolve(document.querySelector(selector));
    }, timeout);
  });

export const useTour = () => {
  const { t } = useI18n();
  const router = useRouter();

  const startTour = (tourId, steps, options = {}) => {
    if (!options.force && isCompleted(tourId)) return null;
    if (!steps || steps.length === 0) return null;

    let driverObj;

    const goToStepRoute = async stepIndex => {
      const step = steps[stepIndex];
      if (!step) return;
      if (step.route) {
        const current = router.currentRoute.value;
        const target = typeof step.route === 'string' ? step.route : step.route.path;
        if (current.path !== target) {
          await router.push(step.route).catch(() => {});
        }
      }
      if (step.element) {
        await waitForElement(step.element, 3000);
      }
    };

    driverObj = driver({
      showProgress: true,
      animate: true,
      smoothScroll: true,
      allowClose: true,
      overlayOpacity: 0.6,
      stagePadding: 4,
      stageRadius: 8,
      progressText: t('TOUR.PROGRESS', { current: '{{current}}', total: '{{total}}' }),
      nextBtnText: t('TOUR.NEXT'),
      prevBtnText: t('TOUR.PREV'),
      doneBtnText: t('TOUR.DONE'),
      onDestroyed: () => markCompleted(tourId),
      onNextClick: async () => {
        const next = driverObj.getActiveIndex() + 1;
        if (next >= steps.length) {
          driverObj.destroy();
          return;
        }
        await goToStepRoute(next);
        driverObj.moveTo(next);
      },
      onPrevClick: async () => {
        const prev = driverObj.getActiveIndex() - 1;
        if (prev < 0) return;
        await goToStepRoute(prev);
        driverObj.moveTo(prev);
      },
      steps,
    });

    goToStepRoute(0).then(() => driverObj.drive());
    return driverObj;
  };

  return {
    startTour,
    isCompleted,
    resetTour: reset,
  };
};
